/** @jsxImportSource theme-ui */
import { jsx } from 'theme-ui'
import { Link } from 'gatsby'
import { useSelector } from 'react-redux'
import styled from '@emotion/styled'
import { Flex, Button, Link as Href, Box } from '@theme-ui/components'
import { slide as SlideMenu } from 'react-burger-menu'
import Menu, { SubMenu, Item as MenuItem } from 'rc-menu'
import { globalHistory as history } from '@reach/router'
import { useResponsiveValue } from '../../../../shared-utils/src/components/utils/media-query'
import menuItems from '../../data/menu.yml'
import logo from '../../images/greythr-logo.svg'
import { Container, Accordion } from '../organisms'
import { RegionalLink } from '../atoms';

const Logo = () => (
  <Link to="/">
    <img sx={{ width: '90px', mr: '10px' }} src={logo} alt="greytHR" />
  </Link>
)

const HeaderCTA = ({ type = 'H' }) => {
  const path = history.location.pathname
  const flexDirection = type === 'H' ? 'row' : 'column'
  const alignSelf = type === 'H' ? 'center' : 'flex-end'
  const alignItems = type === 'H' ? 'center' : 'flex-start'
  const px = type === 'H' ? 3 : 0
  const py = type === 'H' ? 0 : 2
  const mt = type === 'H' ? 0 : 0
  const isDarkMode = useSelector(state => state.app.isDarkMode)
  const region = useSelector(state => state.app.region)
  const fontSize = type == 'H' ? '0.9rem' : 2
  const vertical = type !== 'H'
  return (
    <Flex
      sx={{
        flexDirection,
        border: '0px solid red',
        py,
        mt,
        alignSelf,
        alignItems,
      }}
    >
      {path === '/login' ? null : <HeaderLink to="/login/" title="Login" px={px} mb={1} vertical={vertical} />}
      {path === '/signup' ? null : (
        <Link to="/signup/" sx={{ py }}>
          <Button id="header_signup_btn" sx={{ fontSize, px: 2 }} variant="header">
            SIGNUP
          </Button>
        </Link>
      )}
    </Flex>
  )
}

const Nav = () => (
  <Flex
    sx={{
      justifyContent: 'space-between',
      mb: '-8px',
      alignItems: 'flex-end',
      width: '100%',
      border: '0px solid pink',
    }}
  >
    <NavMenu />
    <HeaderCTA />
  </Flex>
)

const NavMenu = () => (
  <Menu mode="horizontal" sx={{ alignSelf: 'flex-end', border: '0px solid red' }} triggerSubMenuAction="click">
    {menuItems.menu.map(({ title, path, regional, items = [] }, index) => {
      const hasSubMenu = items && items.length > 0
      const t2 = `${title}`
      if (hasSubMenu) {
        return (
          <SubMenu title={title} key={index}>
            {items.map((it, index) => (
              <MenuItem key={index}>
                <HeaderLink to={it.path} key={it.path} title={it.title} regional={regional} />
              </MenuItem>
            ))}
          </SubMenu>
        )
      }
      return (
        <MenuItem key={index}>
          <HeaderLink to={path} key={path} title={title} regional={regional} />
        </MenuItem>
      )
    })}
  </Menu>
)

const BurgerMenuItems = ({ px }) =>
  menuItems.menu.map(({ title, path, regional = false, items = [] }, index) => {
    const hasSubMenu = items && items.length > 0
    if (hasSubMenu) {
      return (
        <Accordion as="h3" key={index}>
          <div
            label={title}
            style={{ marginLeft: 0, pl: 0, border: '0px solid red' }}
            textStyle={{
              color: '#777',
              fontSize: 2,
              pt: 2,
              fontWeight: 'bold',
            }}
          >
            {items.map(({ title, path }, index) => (
              <HeaderLink to={path} regional={regional} key={path} title={title} vertical />
            ))}
          </div>
          <div label="" />
        </Accordion>
      )
    }
    return <HeaderLink to={path} regional={regional} key={path} title={title} px={px} vertical />
  })

const BurgerMenu = () => (
  <div sx={{ border: '1px solid #AEAEAE', p: 1, mx: 3 }}>
    <SlideMenu right styles={styles}>
      <BurgerMenuItems />
      <HeaderCTA type="V" />
    </SlideMenu>
  </div>
)

const HeaderLink = ({ to, title, px, regional, vertical = false }) => {
  const sx = {
    px,
    pb: 0,
    width: ['100%', '100%', 'auto', 'auto'],
    border: '0px solid darkgreen',
    color: '#777',
    backgroundColor: 'unset',
    '&:hover': {
      color: 'primary',
    },
  }
  if (vertical) {
    sx.display = 'block'
    sx.py = 2
    sx.fontSize = 2
  }
  return to.split('/')[0] === 'https:' ? (
    <Href sx={sx} href={to} target="blank">
      {title}
    </Href>
  ) : (
    <RegionalLink sx={sx} to={to} regional={regional} variant="header">
      {title}
    </RegionalLink>
  )
}

const Header = () => {
  const menu = useResponsiveValue([<BurgerMenu />, <BurgerMenu />, <Nav />, <Nav />])
  return (
    <Flex
      sx={{
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: 'headerBackground',
        flexWrap: 'nowrap',
        minHeight: '65px',
        pt: '5px',
        pb: '5px',
        position: 'fixed',
        top: 0,
        width: '100%',
        zIndex: '1030',
        boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.12)',
      }}
      as="div"
    >
      <Container className="container" sx={{ p: 0, border: '0px solid red' }}>
        <StyledHeaderNav as="nav">
          <Logo />
          {menu}
        </StyledHeaderNav>
      </Container>
    </Flex>
  )
}

const StyledHeaderNav = styled(Box)`
  display: flex;
  flex-direction: row;
  padding: 0;
  margin: 0 auto;
  margin: 0;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: nowrap;
  color: #777;
  font-weight: 400;

  @media (min-width: 768px) {
    align-items: center;
    justify-content: flex-start;
  }
`

const styles = {
  bmBurgerButton: {
    width: '24px',
    height: '24px',
    position: 'relative',
  },

  bmBurgerBars: {
    background: '#373a47',
  },
  bmBurgerBarsHover: {
    background: '#adadad',
  },

  bmCrossButton: {
    height: '24px',
    width: '24px',
  },

  bmCross: {
    background: '#bdc3c7',
  },
  bmMenuWrap: {
    position: 'fixed',
    height: '100%',
  },
  bmMenu: {
    background: '#373a47',
    padding: '1.5em 1.5em 0',
    fontSize: '1.15em',
  },
  bmMorphShape: {
    fill: '#373a47',
  },
  bmItemList: {
    color: '#fff',
    padding: '0.1em',
  },
  bmItem: {
    display: 'block',
    padding: '0.9em 0',
  },
  bmOverlay: {
    background: 'rgba(0, 0, 0, 0.4)',
  },
}

export default Header
